/**
 * VRS Client Login/Waiting Room Component
 *
 * A specialized waiting room for deaf/hard-of-hearing clients.
 * Displays video preview, connection status, and interpreter queue information.
 */
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../base/i18n/functions';
import { getBrandWordmarkParts, getTagline } from '../../base/whitelabel/functions';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { getDefaultRequestLanguage } from '../../interpreter-queue/interpreterLanguages';
import { getPersistentItem, setPersistentItem } from '../../vrs-auth/storage';
import { releaseBrowserMediaDevices } from '../mediaCleanup.web';
const useStyles = makeStyles()(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2D1B69 0%, #1a0f3a 100%)',
        color: 'white',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2rem',
        width: '100%',
        maxWidth: '800px',
    },
    logo: {
        fontSize: '2.5rem',
        fontWeight: 300,
        marginBottom: '1rem',
        textAlign: 'center',
    },
    logoMalka: {
        fontStyle: 'italic',
        color: 'white',
    },
    logoVRI: {
        color: '#FF6B35',
        fontWeight: 700,
    },
    subtitle: {
        fontSize: '1rem',
        opacity: 0.9,
        textAlign: 'center',
        marginBottom: '1rem',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '20px',
        padding: '2rem',
        width: '100%',
        maxWidth: '600px',
        backdropFilter: 'blur(10px)',
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '1.5rem',
    },
    videoPreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: 'scaleX(-1)', // Mirror effect
    },
    previewPrompt: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1rem',
        height: '100%',
        color: 'rgba(255, 255, 255, 0.75)',
    },
    previewIcon: {
        fontSize: '3rem',
        opacity: 0.5,
    },
    previewButton: {
        padding: '10px 18px',
        borderRadius: '999px',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.95rem',
        fontWeight: 600,
    },
    statusContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '16px 24px',
        background: 'rgba(255, 107, 53, 0.15)',
        border: '1px solid rgba(255, 107, 53, 0.3)',
        borderRadius: '30px',
        marginBottom: '1rem',
    },
    statusDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: '#FF6B35',
        animation: '$pulse 2s ease-in-out infinite',
    },
    statusText: {
        fontSize: '1.1rem',
        fontWeight: 500,
    },
    statusSubtext: {
        fontSize: '0.9rem',
        opacity: 0.8,
        textAlign: 'center',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        width: '100%',
        marginBottom: '1.5rem',
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1rem',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
    },
    infoLabel: {
        fontSize: '0.75rem',
        opacity: 0.7,
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    infoValue: {
        fontSize: '1rem',
        fontWeight: 500,
    },
    buttonContainer: {
        display: 'flex',
        gap: '1rem',
        width: '100%',
    },
    button: {
        flex: 1,
        padding: '14px 24px',
        border: 'none',
        borderRadius: '12px',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    buttonPrimary: {
        background: '#FF6B35',
        color: 'white',
    },
    buttonPrimaryHover: {
        background: '#e55a2b',
    },
    buttonSecondary: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    footer: {
        marginTop: '1rem',
        textAlign: 'center',
        fontSize: '0.85rem',
        opacity: 0.6,
    },
    '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
    },
    // RTL support
    rtl: {
        direction: 'rtl',
    },
}));
/**
 * Component that renders the Client Waiting Room for VRS.
 * This is where deaf clients wait for an interpreter to be assigned.
 */
const ClientLogin = ({ t, roomName }) => {
    const { classes, cx } = useStyles();
    const [isRTL, setIsRTL] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [interpreterStatus, setInterpreterStatus] = useState('searching');
    const [queuePosition, setQueuePosition] = useState(1);
    const [estimatedWait, setEstimatedWait] = useState(2);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const hasRequestedRef = useRef(false);
    useEffect(() => {
        // Check for RTL language preference
        const savedLang = getPersistentItem('vrs_preferred_language');
        if (savedLang === 'ar') {
            setIsRTL(true);
        }
        const handleRequestQueued = (data) => {
            const position = data.position || 1;
            setQueuePosition(position);
            setEstimatedWait(Math.max(position * 2, 1));
            setInterpreterStatus('searching');
        };
        const handleMatchFound = () => {
            setInterpreterStatus('found');
        };
        const handleMeetingInitiated = () => {
            setInterpreterStatus('connected');
        };
        const handleConnection = (data) => {
            if (data.connected && !hasRequestedRef.current) {
                hasRequestedRef.current = true;
                queueService.requestInterpreter(getDefaultRequestLanguage(), getPersistentItem('vrs_client_name') || 'Guest', roomName);
            }
        };
        queueService.on('requestQueued', handleRequestQueued);
        queueService.on('matchFound', handleMatchFound);
        queueService.on('meetingInitiated', handleMeetingInitiated);
        queueService.on('connection', handleConnection);
        if (!hasRequestedRef.current && queueService.isConnected()) {
            hasRequestedRef.current = true;
            queueService.requestInterpreter(getDefaultRequestLanguage(), getPersistentItem('vrs_client_name') || 'Guest', roomName);
        }
        return () => {
            queueService.off('requestQueued', handleRequestQueued);
            queueService.off('matchFound', handleMatchFound);
            queueService.off('meetingInitiated', handleMeetingInitiated);
            queueService.off('connection', handleConnection);
            // Cleanup video stream
            try {
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => {
                        track.stop();
                        track.enabled = false;
                    });
                    streamRef.current = null;
                }
            }
            catch (err) {
                console.warn('Error cleaning up video stream:', err);
                streamRef.current = null;
            }
        };
    }, [roomName]);
    const startVideoPreview = async () => {
        try {
            const stream = await navigator.mediaDevices?.getUserMedia({
                video: { facingMode: 'user', width: 1280, height: 720 },
                audio: false
            });
            if (!stream) {
                return;
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setVideoReady(true);
            }
        }
        catch (err) {
            console.warn('Could not access camera for preview:', err);
        }
    };
    const handleJoinMeeting = () => {
        releaseBrowserMediaDevices();
        setPersistentItem('vrs_user_role', 'client');
        setPersistentItem('vrs_client_auth', 'true');
        // Navigate to the meeting
        window.location.href = `/${roomName}`;
    };
    const handleCancel = () => {
        releaseBrowserMediaDevices();
        queueService.cancelRequest();
        // Back to the client portal; the old welcome page now redirects there.
        window.location.href = '/';
    };
    const { prefix: brandPrefix, suffix: brandSuffix } = getBrandWordmarkParts();
    return (React.createElement("div", { className: cx(classes.root, isRTL && classes.rtl) },
        React.createElement("div", { className: classes.container },
            React.createElement("div", { className: classes.logo },
                React.createElement("span", { className: classes.logoMalka }, brandPrefix),
                React.createElement("span", { className: classes.logoVRI }, brandSuffix)),
            React.createElement("div", { className: classes.subtitle }, t('vrs.clientWaitingRoom', { service: getTagline() })),
            React.createElement("div", { className: classes.card },
                React.createElement("div", { className: classes.videoContainer },
                    React.createElement("video", { ref: videoRef, autoPlay: true, muted: true, playsInline: true, className: classes.videoPreview, style: { display: videoReady ? 'block' : 'none' } }),
                    !videoReady && (React.createElement("div", { className: classes.previewPrompt },
                        React.createElement("div", { className: classes.previewIcon }, "\uD83D\uDC64"),
                        React.createElement("button", { className: classes.previewButton, onClick: startVideoPreview, type: 'button' }, "Start Camera")))),
                React.createElement("div", { className: classes.statusContainer },
                    React.createElement("div", { className: classes.statusDot }),
                    React.createElement("div", null,
                        React.createElement("div", { className: classes.statusText },
                            interpreterStatus === 'searching' && t('vrs.status.searching'),
                            interpreterStatus === 'found' && t('vrs.status.interpreterFound'),
                            interpreterStatus === 'connected' && t('vrs.status.connected')),
                        React.createElement("div", { className: classes.statusSubtext }, interpreterStatus === 'searching' &&
                            `${t('vrs.queue.position')}: ${queuePosition} • ${t('vrs.queue.wait')}: ~${estimatedWait} ${t('vrs.minutes')}`))),
                React.createElement("div", { className: classes.infoGrid },
                    React.createElement("div", { className: classes.infoItem },
                        React.createElement("div", { className: classes.infoLabel }, t('vrs.room')),
                        React.createElement("div", { className: classes.infoValue }, roomName || '—')),
                    React.createElement("div", { className: classes.infoItem },
                        React.createElement("div", { className: classes.infoLabel }, t('vrs.service')),
                        React.createElement("div", { className: classes.infoValue }, "ASL / English"))),
                React.createElement("div", { className: classes.buttonContainer },
                    React.createElement("button", { className: cx(classes.button, classes.buttonSecondary), onClick: handleCancel }, t('vrs.cancel')),
                    React.createElement("button", { className: cx(classes.button, classes.buttonPrimary), onClick: handleJoinMeeting, disabled: interpreterStatus === 'searching' }, t('vrs.join')))),
            React.createElement("div", { className: classes.footer }, t('vrs.clientHelp')))));
};
function _mapStateToProps(state) {
    return {
        roomName: state['features/base/conference']?.room || ''
    };
}
export default translate(connect(_mapStateToProps)(ClientLogin));
